import React, {useState} from 'react';
import {Dictionary, groupBy} from 'lodash';

import {
    Offer,
    Referral,
} from 'placement/domains';
import {PAGE} from 'placement/const';
import {ProviderOfferReturnedModal, UnmatchReturnOfferModalState} from './ProviderOfferReturnedModal';
import {MarkSelectedMatchModal} from './MarkSelectedMatchModal';
import {GridViewIcon, MenuIcon} from "evergreen-ui";
import {
    isSelectedOfferProviderNotMatchSiblingProvider,
    preferencesForSibling,
} from "./provider-offer-detail-right-rail/common";
import {ProviderOfferDetailGridView} from "./provider-offer-detail-right-rail/ProviderOfferDetailGridView";
import {ProviderOfferDetailListView} from "./provider-offer-detail-right-rail/ProviderOfferDetailListView";
import {BehaviorSubject} from 'rxjs';
import {useObservable} from "../../rxsupport";
import {useHistory} from 'react-router';
import {useSearchParam} from "../../common/hook";

type ProviderOfferDetailRightRailProps = {
    referrals: Referral[];
    recentPage: PAGE;
    refetchReferrals: () => void;
    offersByReferralGuid: Dictionary<Offer[]>;
    selectedReferral$: BehaviorSubject<Referral | null>;
}

export function ProviderOfferDetailRightRail({
                                                 referrals,
                                                 offersByReferralGuid,
                                                 selectedReferral$,
                                                 recentPage,
                                                 refetchReferrals,
                                             }: ProviderOfferDetailRightRailProps) {
    const [selectedItem, setSelectedItem] = React.useState<Offer | null>(null);
    const [unmatchReturnedModalState, setUnmatchReturnedModalState] = useState<UnmatchReturnOfferModalState>('hide');
    const history = useHistory();
    const searchParams = useSearchParam();
    const [isListView, setIsListView] = useState(searchParams.get('listview') === null || searchParams.get('listview') === "true");

    const [root] = referrals;
    const haveSibling = referrals.length > 1;

    const otherReferralsHasSelectedOfferCount = referrals.filter((referral) =>
        selectedItem != null && selectedItem?.referralGuid !== referral.guid && referral.matchedOffer != null).length;

    const selectedReferral = useObservable(selectedReferral$, selectedReferral$.getValue());

    const groupedReferral = groupBy(referrals, (r) => r.guid);

    const prefsSibling = preferencesForSibling(offersByReferralGuid, recentPage);

    // cancel selected by esc button
    const escFunction = React.useCallback((event) => {
        if (event.keyCode === 27) {
            setSelectedItem(null);
        }
    }, []);

    const setIsListViewWithUpdatedUrl = (isLV: boolean) => {
        searchParams.set("listview", isLV.toString());
        // @ts-ignore
        history.replace({search: searchParams.toString(), state: history.location.state});
        setIsListView(isLV);
    }

    React.useEffect(() => {
        document.addEventListener("keydown", escFunction, false);

        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    return (
        <div className='h-full pr-5 float-right flex flex-col placement-provider-offer-detail-right-rail-width'
             data-testid="provider-offer-detail-right-rail"
        >
            <div className='flex-grow-0 py-5 flex justify-between'>
                <div className='font-semibold text-xl'>
                    Providers
                </div>
                <div className='flex items-center'>
                    {haveSibling &&
                        <div className='flex pr-5'>
                            <div
                                className={`items-center flex justify-center ${isListView ? 'bg-blue-portal-link rounded' : ''}`}
                                onClick={() => setIsListViewWithUpdatedUrl(true)}
                                style={{height: 32, width: 44}}>
                                <MenuIcon size={16} color={isListView ? '#FFFFFF' : '#5566FE'}/>
                            </div>
                            <div className="pl-4"/>
                            <div
                                className={`items-center flex justify-center ${isListView ? '' : 'bg-blue-portal-link rounded'}`}
                                onClick={() => setIsListViewWithUpdatedUrl(false)}
                                style={{height: 32, width: 44}}>
                                <GridViewIcon size={16} color={isListView ? '#5566FE' : '#FFFFFF'}/>
                            </div>
                        </div>
                    }
                </div>
            </div>
            {isListView && <>
                <ProviderOfferDetailListView offers={offersByReferralGuid[selectedReferral!.guid] ?? []}
                                             referral={selectedReferral!}
                                             selectedItem={selectedItem}
                                             setSelectedItem={setSelectedItem}
                                             setUnmatchReturnModalState={setUnmatchReturnedModalState}
                                             prefsSibling={prefsSibling}
                                             recentPage={recentPage}/>
            </>
            }
            {
                !isListView &&
                <ProviderOfferDetailGridView offersByReferralGuid={offersByReferralGuid}
                                             referralsGroupByGuid={groupedReferral} recentPage={recentPage}
                                             selectedItem={selectedItem} setSelectedItem={setSelectedItem}
                                             prefsSibling={prefsSibling}
                                             setUnmatchReturnModalState={setUnmatchReturnedModalState}
                                             rootGuid={root.guid}/>
            }
            <ProviderOfferReturnedModal modalState={unmatchReturnedModalState}
                                        setHideModal={() => setUnmatchReturnedModalState('hide')}
                                        referral={selectedReferral!} selectedOffer={selectedItem}/>
            <MarkSelectedMatchModal selectedItem={selectedItem}
                                    setSelectedItem={setSelectedItem}
                                    setUnmatchReturnModalState={setUnmatchReturnedModalState}
                                    selectedProviderNotMatchSiblingProvider={
                                        isSelectedOfferProviderNotMatchSiblingProvider(
                                            groupedReferral,
                                            offersByReferralGuid,
                                            selectedItem,
                                            recentPage
                                        )}
                                    recentPage={recentPage}
                                    referrals={isListView ? [selectedReferral!] : referrals}
                                    isOtherReferralsHasOffer={otherReferralsHasSelectedOfferCount === (referrals.length - 1)}
                                    refetchReferrals={refetchReferrals}
            />
        </div>
    );
}